import React, { useState, useRef, useEffect } from "react";
import {
  Flex,
  Text,
  Circle,
  Spinner,
  Card,
  Button,
  DrawerFooter,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerCloseButton,
  DrawerBody,
  Icon,
  Box,
  VStack,
  Image,
  Center,
} from "@chakra-ui/react";
import { ReceiptCard, variants } from "../ConfirmationTemplates";
import { OptionsButton } from "./DynamicButton";
import { WarningIcon } from "@chakra-ui/icons";
import PaymentConfirmation from "./PaymentConfirmation";
import { renderMessageWithLinks, titleCase } from "../../utils/functions";
import { AiFillCheckCircle } from "react-icons/ai";
import collectLogo from "../../assets/img/nobukcollect.png";
import { generateReceipt } from "../Content/pdfMaker";
import Footer from "./Footer";
import { handleGenerateStatements } from "../../utils/api";
import { createFileName, useScreenshot } from "use-react-screenshot";
import html2canvas from "html2canvas";
import ShareScreenshot from "./ShareScreenshot";
import { FaDownload, FaShareAlt } from "react-icons/fa";
import CONFIRMATION_TEMPLATES from "../../data/confirmationTemplates";

const ConfirmModal = ({
  isToOpen,
  handleCloseThisModal,
  updating,
  setIsToOpen,
  orgName,
  paymentStatus,
  amount,
  paidBy,
  phoneNumberBy,
  transactionCode,
  colorMain,
  handleCardClick,
  handlePaymentRecheck,
  handleRepay,
  name,
  selection,
  enabled,
  customMessage,
  payType,
  downloadMessage,
  details,
  downloadPdf,
  collection_type,
  toast,
  footerData,
}) => {
  const [processing, setProcessing] = useState(false);

  const ref = useRef(null);
  const refs = useRef(null);

  const [image, takeScreenshot] = useScreenshot({
    type: "image/png",
    quality: 1.0,
  });

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [downloading, setIsDownloading] = useState(false);
  const [receipt, setReceipt] = useState(true);

  // Defaults to Neo-Brutalist unless the collection's alpha_type picks a specific style
  const variant = variants.includes(details?.alpha_type)
    ? details.alpha_type
    : "neo-brutalist";
  const templateHtml = CONFIRMATION_TEMPLATES[variant];

  const getImage = async () => {
    if (receipt) {
      setReceipt(false);
      return;
    }

    setIsDownloading(true);

    try {
      const dataUri = await takeScreenshot(refs.current);
      downloadImage(dataUri);
    } catch (error) {
      console.error("Error capturing screenshot:", error);
    }

    setIsDownloading(false);
    setReceipt(true);
  };

  useEffect(() => {
    if (!receipt) {
      getImage();
    }
  }, [receipt]);

  const getThisImage = async () => {
    setIsDownloading(true);
    try {
      await takeScreenshot(refs.current);
    } catch (error) {
      console.error("Error capturing screenshot:", error);
    }
    setIsDownloading(false);
  };

  const downloadImage = (dataUri) => {
    setIsDownloading(true);
    const link = document.createElement("a");
    link.href = dataUri;
    link.download = "receipt.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsDownloading(false);
  };

  return (
    <Drawer
      isOpen={isToOpen}
      placement="bottom"
      closeOnOverlayClick={false}
      onClose={() => {
        handleCloseThisModal(setIsToOpen);
      }}
      isCentered
      size={["xl", "xl"]}
      display="flex"
      alignItems="center"
      flexDir="column"
      p={8}
    >
      <DrawerOverlay bg="rgba(0, 0, 0, 0.4)" backdropFilter="blur(10px)" />
      <DrawerContent
        bg="transparent"
        shadow="none"
        borderTopRightRadius={24}
        borderTopLeftRadius={24}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {!updating && !processing && (
          <DrawerCloseButton
            size="lg"
            fontWeight="bold"
            bg="white"
            borderRadius="full"
            m={4}
            color="#062a30"
            zIndex={999}
            onClick={() => handleCloseThisModal(setIsToOpen)}
            _hover={{ bg: "gray.100" }}
          />
        )}
        <DrawerBody
          w="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          {updating ? (
            <Flex
              h="300px"
              w="100%"
              bg="white"
              borderRadius="3xl"
              flexDir="column"
              alignItems="center"
              justifyContent="center"
              shadow="2xl"
            >
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="#2fb5b0"
                size="xl"
              />
              <Text fontSize="md" mt={4} fontWeight="medium" color="gray.600">
                Verifying payment status...
              </Text>
            </Flex>
          ) : (
            <VStack spacing={6} w="100%">
              {paymentStatus ? (
                <Box w="100%" maxW="md" ref={refs}>
                  <Center mb={4}>
                    <Image src={collectLogo} w="100px" alt="Nobuk Collect" />
                  </Center>
                  <ReceiptCard
                    title="PAYMENT COMPLETE"
                    html={templateHtml}
                    data={{
                      amount: amount !== undefined
                        ? Number(amount).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                        : "0.00",
                      currency: "KES",
                      paidBy: titleCase(paidBy || "Anonymous"),
                      fromAccount: phoneNumberBy || "N/A",
                      transactionCode: transactionCode || "N/A",
                      date: new Date().toLocaleDateString(),
                      baseImage: details?.sale_settings?.base_image,
                      customerMessage: customMessage || details?.sale_settings?.customer_message,
                    }}
                  />
                </Box>
              ) : (
                <Flex
                  flexDir="column"
                  bg="white"
                  w="100%"
                  maxW="md"
                  borderRadius="3xl"
                  p={8}
                  shadow="2xl"
                  alignItems="center"
                  position="relative"
                  ref={refs}
                >
                  <Circle bg="#2fb5b0" size="16" mb={4} color="white">
                    <Icon as={WarningIcon} boxSize={8} color="white" />
                  </Circle>
                  {/* ... rest of pending state ... */}
                  <Text fontSize="xl" fontWeight="bold" color="#062a30" textAlign="center" mb={1} textTransform="uppercase">
                    PAYMENT PENDING
                  </Text>
                  <Text color="gray.500" fontSize="sm" textAlign="center" mb={6}>
                    We are checking your payment status
                  </Text>
                  <Text color="gray.500" textAlign="center" mb={8}>
                    Please wait while we confirm your transaction.
                  </Text>
                  <OptionsButton
                    colorMain="#062a30"
                    buttonText="Close"
                    isLoading={updating}
                    isDisabled={updating}
                    onClick={() => {
                      handleCloseThisModal(setIsToOpen);
                    }}
                  />
                </Flex>
              )}

              {/* Action Buttons (Only show when complete) */}
              {paymentStatus && (
                <VStack w="100%" maxW="md" spacing={4} p={4} bg="transparent">
                  {enabled ? (
                    <Button
                      bg="#062a30"
                      color="white"
                      size="lg"
                      w="100%"
                      py={7}
                      borderRadius="xl"
                      _hover={{ bg: "#000000", transform: "translateY(-1px)", shadow: "lg" }}
                      _active={{ bg: "#062a30", transform: "translateY(0)" }}
                      fontSize="md"
                      fontWeight="semibold"
                      leftIcon={
                        downloadMessage &&
                          downloadMessage.toLowerCase().includes("statement") ? (
                          <Icon as={FaShareAlt} />
                        ) : (
                          <Icon as={FaDownload} />
                        )
                      }
                      isDisabled={processing}
                      isLoading={processing}
                      loadingText="Processing..."
                      onClick={() => {
                        if (
                          downloadMessage &&
                          downloadMessage
                            .toLowerCase()
                            .includes("statement" || "statements")
                        ) {
                          window.open(`/statement/${transactionCode}`, "_blank");
                        } else {
                          window.open(`/receipt/${transactionCode}`, "_blank");
                        }
                      }}
                      boxShadow="0 4px 14px 0 rgba(0,118,255,0.39)"
                    >
                      {downloadMessage || "Get Proof of Payment"}
                    </Button>
                  ) : (
                    <Text fontWeight="semibold" textAlign="center" color="#062a30">
                      {customMessage
                        ? renderMessageWithLinks(customMessage)
                        : "Thank You!"}
                    </Text>
                  )}

                  <Button
                    variant="ghost"
                    color="gray.500"
                    size="sm"
                    fontWeight="normal"
                    onClick={() => getImage()}
                    isDisabled={downloading}
                  >
                    Download as Image
                  </Button>
                </VStack>
              )}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default ConfirmModal;
